import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Breadcrumbs, Button, CircularProgress, Link, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AuditStatusChip } from "../AuditHub/components/AuditCells";
import { daysRemainingTone, formatDaysRemaining, formatShortDate, scorePercent } from "../AuditHub/utils";
import { AH_ACCENT, AH_MUTED, AH_TEXT, MetaSeparator, backButtonSx } from "../AuditHub/theme";
import { frameworkMatches } from "./ControlAuditPanel";

const DUE_COLOR = { overdue: "#dc2626", warn: "#d97706" };

const factSx = { fontSize: 13, color: AH_MUTED, flexShrink: 0, whiteSpace: "nowrap" };

/** Visible assessment name and owner stay within this many characters. */
const VALUE_LIMIT = 10;

const valueSx = {
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: "22px",
  flexShrink: 0,
  whiteSpace: "nowrap",
  cursor: "default",
  borderRadius: "4px",
  "&:focus-visible": { outline: `2px solid ${AH_ACCENT}`, outlineOffset: 2 },
};

const clipToLimit = (value) => {
  const chars = Array.from(value);
  if (chars.length <= VALUE_LIMIT) return value;
  return `${chars.slice(0, VALUE_LIMIT).join("")}...`;
};

/** Plain text for a context value: 10 characters on screen, full value in the tooltip. */
const TruncatedFact = ({ label, value, emptyTip, prefix, plain = false }) => {
  const full = String(value ?? "").trim();
  const tip = full || emptyTip;
  const shown = full ? clipToLimit(full) : "—";
  return (
    <Tooltip title={tip} arrow placement="bottom">
      <Typography
        component="span"
        tabIndex={0}
        aria-label={`${label}: ${tip}`}
        sx={plain ? { ...valueSx, fontWeight: 400, color: AH_MUTED } : valueSx}
      >
        {prefix}
        {shown}
      </Typography>
    </Tooltip>
  );
};

const scoreLabel = (value) => {
  const percent = scorePercent(value);
  if (percent == null) return "Scan in progress";
  return `${Math.round(percent)}%`;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Account option state for the current framework: the view needs that account's scope and a finished run. */
const accountAvailability = (account, compliance) => {
  const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
  if (!scope) return { scope: null, reason: "Framework not assessed on this account", pendingScan: false };
  if (!scope.latestRunId) return { scope, reason: "Pending Automated Scan Completion", pendingScan: true };
  return { scope, reason: "", pendingScan: false };
};

/** One row: breadcrumb, audit name and summary, and Back - in the Audit Hub style. */
const AuditContextBar = ({ auditId, state }) => {
  const history = useHistory();

  if (!auditId) return null;

  const audit = state?.audit;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const accountCount = audit?.accountCount ?? accounts.length;
  const loading = state?.status === "loading" || state?.status === "idle";
  const goToAuditHub = () => history.push("/audit-hub");

  const remaining = audit?.dueDate ? formatDaysRemaining(audit) : "—";
  const scoreValue = scoreLabel(audit?.auditScore);
  const scorePending = scoreValue === "Scan in progress";
  const facts = [
    { key: "accounts", text: plural(accountCount, "account") },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 1,
        flexWrap: { xs: "wrap", md: "nowrap" },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: 1.25,
          rowGap: 0.5,
          flexWrap: "wrap",
          minWidth: 0,
          flex: "1 1 auto",
        }}
      >
        <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED, flexShrink: 0 }}>
          <Link component="button" underline="hover" color="inherit" onClick={goToAuditHub} sx={{ fontSize: 13 }}>
            Assessments
          </Link>
          <Typography sx={{ fontSize: 13, color: AH_TEXT }}>{audit?.auditCode || auditId}</Typography>
        </Breadcrumbs>
        <MetaSeparator hideOnMobile />
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <CircularProgress size={14} />
            <Typography noWrap sx={{ fontSize: 13, color: AH_MUTED }}>Loading assessment…</Typography>
          </Box>
        ) : state?.status === "error" ? (
          <Typography sx={{ fontSize: 13, color: "#b91c1c", minWidth: 0, overflowWrap: "anywhere" }}>{state.error}</Typography>
        ) : (
          <>
            <TruncatedFact label="Assessment Name" value={audit?.name} emptyTip="No assessment name" />
            {audit?.status ? (
              <>
                <MetaSeparator />
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  <AuditStatusChip status={audit.status} />
                </Box>
              </>
            ) : null}
            <MetaSeparator />
            <TruncatedFact label="Owner" value={audit?.ownerEmail} emptyTip="No owner assigned" prefix="Owner: " plain />
            <MetaSeparator />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1.25,
                rowGap: 0.5,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                flexShrink: 0,
                maxWidth: "100%",
              }}
            >
              <Typography sx={factSx}>
                Score:{" "}
                {scorePending ? (
                  <Box component="span" sx={{ color: AH_ACCENT }}>{scoreValue}</Box>
                ) : (
                  scoreValue
                )}
              </Typography>
              {facts.map((item) => (
                <React.Fragment key={item.key}>
                  <MetaSeparator />
                  <Typography sx={factSx}>{item.text}</Typography>
                </React.Fragment>
              ))}
              {audit?.dueDate ? (
                <>
                  <MetaSeparator />
                  <Typography sx={factSx}>
                    Due Date: {formatShortDate(audit.dueDate)}
                    {remaining !== "—" ? (
                      <>
                        {" ("}
                        <Box
                          component="span"
                          sx={{ color: DUE_COLOR[daysRemainingTone(audit)] || "inherit", fontWeight: 600 }}
                        >
                          {remaining}
                        </Box>
                        {")"}
                      </>
                    ) : null}
                  </Typography>
                </>
              ) : null}
            </Box>
          </>
        )}
      </Box>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goToAuditHub} sx={backButtonSx}>
        Back to Audit Hub
      </Button>
    </Box>
  );
};

export { accountAvailability };
export default AuditContextBar;
